import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientUpdates } from '../api/clientUpdates';
import { refusalMessage } from '../api/errors';
import './notificationsPremium.css';

/**
 * The client's Notifications (Rio, 2 Oct 2026): their own delivery updates
 * from InGo (`GET customer/me/updates`), the same words their phone was told,
 * newest first; a tap opens the delivery. What has been read is kept on this
 * phone (the server keeps no read state): the newest update seen.
 */

const SEEN_KEY = 'ingo_updates_seen_up_to';

const TYPE_STYLE = {
  accepted: { bg: '#07408F', icon: 'rider' },
  picked_up: { bg: '#EC6C23', icon: 'package' },
  delivered: { bg: '#16a34a', icon: 'check' },
  cancelled: { bg: '#dc2626', icon: 'cancel' },
  requested: { bg: '#EC6C23', icon: 'package' },
};

function readSeen() {
  try {
    return Number(localStorage.getItem(SEEN_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeSeen(id) {
  try {
    localStorage.setItem(SEEN_KEY, String(id));
  } catch {
    // Private mode: read state lasts this visit only.
  }
}

/** "Today", "Yesterday" or "Earlier", on this phone's calendar. */
function groupOf(iso) {
  const at = new Date(iso);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (at >= start) return 'today';
  start.setDate(start.getDate() - 1);

  return at >= start ? 'yesterday' : 'earlier';
}

function when(iso, group) {
  const at = new Date(iso);
  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (group === 'today') return time;
  if (group === 'yesterday') return `Yesterday · ${time}`;

  return `${at.toLocaleDateString([], { day: 'numeric', month: 'short' })} · ${time}`;
}

const GROUPS = [
  ['today', 'TODAY'],
  ['yesterday', 'YESTERDAY'],
  ['earlier', 'EARLIER'],
];

function BackArrow() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path d="M15.5 18.5L8.5 12l7-7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Icon({ name }) {
  const common = { viewBox: '0 0 24 24', width: 20, height: 20, fill: 'none', stroke: 'currentColor', 'aria-hidden': true };
  if (name === 'check') {
    return (
      <svg {...common} strokeWidth="1.8">
        <path d="M6 12.5l3.5 3.5L18 7.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === 'cancel') {
    return (
      <svg {...common} strokeWidth="1.8">
        <path d="M8 8l8 8M16 8l-8 8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === 'rider') {
    return (
      <svg {...common} strokeWidth="1.6">
        <circle cx="6" cy="17" r="2.5" />
        <circle cx="18" cy="17" r="2.5" />
        <path d="M8.5 17h5l3-6h-4l-2 3H6M14 7h2.5l1.5 4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg {...common} strokeWidth="1.6">
      <path d="M12 3 4 7.5v9L12 21l8-4.5v-9L12 3Z" strokeLinejoin="round" />
      <path d="M12 12 20 7.5M12 12V21M12 12 4 7.5" strokeLinecap="round" />
    </svg>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState(null);
  const [error, setError] = useState('');
  const [seen, setSeen] = useState(readSeen);

  const load = useCallback(async () => {
    try {
      setUpdates(await clientUpdates.list());
      setError('');
    } catch (e) {
      setError(refusalMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
    // An update pushed while this screen is open: read again.
    const onUpdate = () => void load();
    window.addEventListener('ingo-client-update', onUpdate);

    return () => window.removeEventListener('ingo-client-update', onUpdate);
  }, [load]);

  const grouped = useMemo(() => {
    const out = { today: [], yesterday: [], earlier: [] };
    for (const u of updates || []) out[groupOf(u.at)].push(u);

    return out;
  }, [updates]);

  const newest = updates && updates.length > 0 ? updates[0].id : 0;
  const markAllRead = () => {
    writeSeen(newest);
    setSeen(newest);
  };

  return (
    <div className="ntf-page" role="main" aria-label="Notifications">
      <header className="ntf-nav">
        <button type="button" className="ntf-nav__back" onClick={() => navigate(-1)} aria-label="Back">
          <BackArrow />
        </button>
        <h1 className="ntf-nav__title">Notifications</h1>
        <button type="button" className="ntf-nav__mark" onClick={markAllRead} disabled={!newest || newest <= seen}>
          Mark all as read
        </button>
      </header>

      <div className="ntf-scroll">
      {error ? (
        <p className="ntf-empty__sub" role="alert" style={{ padding: '12px 16px' }}>
          {error}
        </p>
      ) : null}

      {updates === null && !error ? (
        <p className="ntf-empty__sub" role="status" style={{ padding: '12px 16px' }}>
          Loading your updates…
        </p>
      ) : null}

      {updates && updates.length === 0 ? (
        <div className="ntf-empty">
          <h2 className="ntf-empty__title">No updates yet</h2>
          <p className="ntf-empty__sub">When a rider takes your delivery, picks it up and delivers it, you will see it here.</p>
        </div>
      ) : null}

      {GROUPS.map(([key, label]) =>
        grouped[key].length > 0 ? (
          <section key={key} aria-label={label}>
            <h2 className="ntf-group-label">{label}</h2>
            {grouped[key].map((u) => {
              const style = TYPE_STYLE[u.status] || TYPE_STYLE.requested;
              const unread = u.id > seen;

              return (
                <button
                  key={u.id}
                  type="button"
                  className={unread ? 'ntf-card ntf-card--unread' : 'ntf-card'}
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => navigate(`/live-tracking?delivery=${u.delivery_id}`)}
                >
                  <span className="ntf-card__badge" style={{ background: style.bg }} aria-hidden>
                    <Icon name={style.icon} />
                  </span>
                  <span className="ntf-card__body">
                    <span className="ntf-card__title" style={{ display: 'block' }}>{u.title}</span>
                    <span className="ntf-card__sub" style={{ display: 'block' }}>{u.body}</span>
                    <span className="ntf-card__ago" style={{ display: 'block' }}>{when(u.at, key)}</span>
                  </span>
                  {unread ? <span className="ntf-card__dot" aria-label="Unread" /> : null}
                </button>
              );
            })}
          </section>
        ) : null,
      )}
      </div>
    </div>
  );
}
